import { useMutation } from '@tanstack/react-query'
import { EditIcon, Modal } from '@viapeople/components'
import { useState } from 'react'
import { useToast } from '../../../../utils/ToastContext'
import { queryClient, trpc } from '../../../../utils/trpc'

interface EditableReportFieldProps {
	taskId: string
	componentId: string
	currentValue: string
	label: string
	renderField: (value: string, onChange: (value: string) => void, readOnly: boolean) => React.ReactNode
	onSuccess?: () => void
}

export const EditableReportField = ({
	taskId,
	componentId,
	currentValue,
	label,
	renderField,
	onSuccess,
}: EditableReportFieldProps) => {
	const [isEditing, setIsEditing] = useState(false)
	const [editValue, setEditValue] = useState(currentValue)
	const { showToast } = useToast()

	const overwriteMutation = useMutation({
		...trpc.client.tasks.overwriteForm.mutationOptions(),
		onSuccess: () => {
			showToast('Response updated successfully')
			setIsEditing(false)

			// Invalidate queries to refresh the data
			const actualQueryKey = trpc.client.tasks.show.queryOptions({
				taskId,
			}).queryKey
			queryClient.invalidateQueries({
				queryKey: actualQueryKey,
			})
			queryClient.invalidateQueries({
				queryKey: ['cycleExecution', 'show'],
			})

			onSuccess?.()
		},
		onError: (error) => {
			console.error('Error updating response:', error)
			showToast('Failed to update response', 'error')
		},
	})

	const handleSave = () => {
		overwriteMutation.mutate({
			taskId,
			responses: [
				{
					componentId,
					response: editValue,
				},
			],
		})
	}

	const handleCancel = () => {
		setEditValue(currentValue)
		setIsEditing(false)
	}

	return (
		<>
			<button
				type='button'
				className='absolute -right-10 top-1.5 p-1.5 rounded-full bg-primary-100 hover:bg-primary-300'
				onClick={() => {
					setEditValue(currentValue)
					setIsEditing(true)
				}}
				title='Edit response'>
				<EditIcon size={16} />
			</button>

			<Modal
				isOpen={isEditing}
				title={`Edit ${label}`}
				onClose={handleCancel}
				fullContent={false}
				buttons={[
					{
						children: 'Cancel',
						variant: 'secondary',
						onClick: handleCancel,
					},
					{
						children: overwriteMutation.isPending ? 'Saving...' : 'Save',
						variant: 'primary',
						onClick: handleSave,
						disabled: overwriteMutation.isPending,
					},
				]}>
				<div className='p-4'>{renderField(editValue, setEditValue, false)}</div>
			</Modal>
		</>
	)
}
